<template>
  <div>
    <header class="header">
      <h1 class="text-light">{{ $t('guide.title') }}</h1>
      <p class="header-subtitle">{{ $t('guide.subtitle') }}</p>
    </header>
    <div class="container-nzidoo">

      <!-- Slider for all screen sizes -->
      <div class="steps-container-wrapper">
        <div class="steps-container" ref="sliderWrapper">
          <div class="step-slide" v-for="(step, index) in steps" :key="index">
            <div class="container">
              <div class="row">
                <div class="col-lg-12">
                  <div class="step-card">
                    <img class="step-image" :src="step.image" :alt="`Step ${index + 1} illustration`" />
                    <h2 class="title"> {{ $t(step.title) }}</h2>
                    <p class="step-description">{{ $t(step.description) }}</p>

                    <h3>{{ $t('guide.how') }}</h3>
                    <ul>
                      <li v-for="(item, i) in step.items" :key="i">
                        {{ $t(item) }}
                      </li>
                    </ul>

                    <div class="tip-box" v-if="step.tip">
                      <strong>{{ $t(step.tip) }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Controls -->
        <div class="slider-controls">
          
          <div class="dots-indicator">
            <span 
              v-for="(step, index) in steps" 
              :key="index" 
              class="dot"
              :class="{ active: currentSlide === index }"
              @click="scrollToSlide(index)"
            ></span>
          </div>

        </div>
      </div>

      <div class="cta-container">
        <router-link :to="{ name: 'productSell' }" class="btn btn-primary">
          {{ $t('sentence.sellNow') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import img1 from '@/assets/img/how_it_works/Comment_ca_marche_1.webp'
import img2 from '@/assets/img/how_it_works/Comment_ca_marche_2.webp'
import img3 from '@/assets/img/how_it_works/Comment_ca_marche_3.webp'
import img4 from '@/assets/img/how_it_works/Comment_ca_marche_4.webp'

export default {
  name: "SellingStepsExplanation",
  data() {
    return {
      touchStartX: 0,
      touchEndX: 0,
      isDragging: false,
      currentSlide: 0,
      steps: [
        {
          title: 'guide.step1.title',
          description: 'guide.step1.description',
          items: [
            'guide.step1.items.1',
            'guide.step1.items.2',
            'guide.step1.items.3',
            'guide.step1.items.4'
          ],
          tip: 'guide.step1.tip',
          image: img1
        },
        {
          title: 'guide.step2.title',
          description: 'guide.step2.description',
          items: [
            'guide.step2.items.1',
            'guide.step2.items.2',
            'guide.step2.items.3',
            'guide.step2.items.4',
            'guide.step2.items.5',
            'guide.step2.items.6'
          ],
          tip: 'guide.step2.tip',
          image: img2
        },
        {
          title: 'guide.step3.title',
          description: 'guide.step3.description',
          items: [
            'guide.step3.items.1',
            'guide.step3.items.2',
            'guide.step3.items.3',
            'guide.step3.items.4'
          ],
          tip: 'guide.step3.tip',
          image: img3
        },
        {
          title: 'guide.step4.title',
          description: 'guide.step4.description',
          items: [
            'guide.step4.items.1',
            'guide.step4.items.2',
            'guide.step4.items.3',
            'guide.step4.items.4'
          ],
          tip: 'guide.step4.tip',
          image: img4
        }
      ]
    }
  },
  methods: {
    updateCurrentSlide() {
      const slider = this.$refs.sliderWrapper;
      if (!slider) return;
      
      const scrollLeft = slider.scrollLeft;
      const slideWidth = slider.querySelector('.step-slide').offsetWidth;
      this.currentSlide = Math.round(scrollLeft / slideWidth);
    },

    scrollToSlide(index) {
      const slider = this.$refs.sliderWrapper;
      if (!slider) return;
      
      const slideWidth = slider.querySelector('.step-slide').offsetWidth;
      slider.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    },

    nextSlide() {
      if (this.currentSlide < this.steps.length - 1) {
        this.scrollToSlide(this.currentSlide + 1);
      }
    },

    previousSlide() {
      if (this.currentSlide > 0) {
        this.scrollToSlide(this.currentSlide - 1);
      }
    },

    handleTouchStart(e) {
      this.isDragging = true;
      this.touchStartX = e.type.startsWith('touch')
        ? e.touches[0].clientX
        : e.clientX;
      this.touchEndX = 0;
    },

    handleTouchMove(e) {
      if (!this.isDragging) return;
      
      this.touchEndX = e.type.startsWith('touch')
        ? e.touches[0].clientX
        : e.clientX;
    },

    handleTouchEnd() {
      if (!this.isDragging) return;
      
      const diff = this.touchStartX - this.touchEndX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0 && this.currentSlide < this.steps.length - 1) {
          this.scrollToSlide(this.currentSlide + 1);
        } else if (diff < 0 && this.currentSlide > 0) {
          this.scrollToSlide(this.currentSlide - 1);
        }
      }

      this.isDragging = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
    },

    handleKeyboard(e) {
      if (e.key === 'ArrowLeft') {
        this.previousSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    }
  },
  mounted() {
    const slider = this.$refs.sliderWrapper;
    if (slider) {
      // Touch events
      slider.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      slider.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      slider.addEventListener('touchend', this.handleTouchEnd);

      // Mouse events
      slider.addEventListener('mousedown', this.handleTouchStart);
      slider.addEventListener('mousemove', this.handleTouchMove);
      slider.addEventListener('mouseup', this.handleTouchEnd);
      slider.addEventListener('mouseleave', this.handleTouchEnd);

      // Scroll event to update current slide
      slider.addEventListener('scroll', this.updateCurrentSlide);
    }

    // Keyboard navigation
    window.addEventListener('keydown', this.handleKeyboard);
  },
  beforeDestroy() {
    const slider = this.$refs.sliderWrapper;
    if (slider) {
      slider.removeEventListener('touchstart', this.handleTouchStart);
      slider.removeEventListener('touchmove', this.handleTouchMove);
      slider.removeEventListener('touchend', this.handleTouchEnd);

      slider.removeEventListener('mousedown', this.handleTouchStart);
      slider.removeEventListener('mousemove', this.handleTouchMove);
      slider.removeEventListener('mouseup', this.handleTouchEnd);
      slider.removeEventListener('mouseleave', this.handleTouchEnd);
      
      slider.removeEventListener('scroll', this.updateCurrentSlide);
    }

    window.removeEventListener('keydown', this.handleKeyboard);
  }
}
</script>

<style scoped>
/* Header */
.header {
  background-color: var(--color-nz);
  color: white;
  padding: 40px 20px;
  text-align: center;
  margin-bottom: 0;
}

.header h1 {
  margin: 0 0 10px 0;
  font-size: 1.8em;
  font-weight: 700;
}

.header-subtitle {
  margin: 0;
  font-size: 1.1em;
  font-weight: 400;
  opacity: 0.95;
  color: white;
}
.title{
  color: var(--color-nz);
}

/* Slider Container */
.steps-container-wrapper {
  padding: 40px 0;
  position: relative;
}

.steps-container {
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scroll-behavior: smooth;
}

.steps-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Step Slide */
.step-slide {
  flex: 0 0 100%;
  scroll-snap-align: start;
  padding: 0 15px;
}

/* Step Card */
.step-card {
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 40px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
}

.step-card h2 {
  font-size: 1.8em;
  color: var(--color-nz);
  border-bottom: 3px solid var(--color-nz-200);
  padding-bottom: 10px;
  margin-top: 0;
  margin-bottom: 20px;
}

.step-image {
  width: 100%;
  height: auto;
  max-height: 250px;
  object-fit: contain;
  border-radius: 10px;
  margin-bottom: 30px;
  display: block;
}

.step-description {
  color: var(--color-secondary);
  font-size: 1.1em;
  margin-bottom: 25px;
  line-height: 1.6;
}

.step-card h3 {
  font-size: 1.3em;
  color: var(--color-secondary);
  margin-top: 25px;
  margin-bottom: 20px;
  font-weight: 600;
}

.step-card ul {
  padding-left: 25px;
  margin-bottom: 25px;
  flex-grow: 1;
}

.step-card li {
  margin-bottom: 12px;
  color: var(--color-secondary);
  line-height: 1.5;
}

/* Tip Box */
.tip-box {
  background-color: #ecf0f1;
  border-left: 5px solid var(--color-nz);
  padding: 20px;
  border-radius: 5px;
  font-style: italic;
  margin-top: auto;
}

.tip-box strong {
  font-weight: 600;
}

/* Slider Controls */
.slider-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 30px 0;
}

.btn-slider {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--color-nz);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5em;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.btn-slider:hover:not(:disabled) {
  background-color: var(--color-nz);
  transform: scale(1.1);
}

.btn-slider:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
  opacity: 0.5;
}

.btn-slider span {
  line-height: 1;
}

/* Dots Indicator */
.dots-indicator {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #bdc3c7;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot:hover {
  background-color: #95a5a6;
  transform: scale(1.2);
}

.dot.active {
  background-color: var(--color-nz);
  transform: scale(1.4);
}

/* CTA Container */
.cta-container {
  text-align: center;
  padding: 40px 20px;
}

/* Responsive styles */
@media (min-width: 768px) {
  .header h1 {
    font-size: 2.5em;
  }

  .header-subtitle {
    font-size: 1.3em;
  }

  .step-card {
    padding: 50px;
    min-height: 550px;
  }

  .step-card h2 {
    font-size: 2em;
  }

  .step-image {
    max-height: 300px;
  }

  .step-description {
    font-size: 1.2em;
  }

  .step-card h3 {
    font-size: 1.4em;
  }

  .step-card li {
    font-size: 1.05em;
  }
}

@media (min-width: 1200px) {
  .step-card {
    padding: 60px 80px;
  }
}

@media (max-width: 767px) {
  .header h1 {
    font-size: 1.5em;
  }

  .header-subtitle {
    font-size: 1em;
  }

  .step-card {
    padding: 25px;
    min-height: 450px;
  }

  .step-card h2 {
    font-size: 1.4em;
  }

  .step-image {
    max-height: 180px;
  }

  .btn-slider {
    width: 40px;
    height: 40px;
    font-size: 1.2em;
  }

  .dot {
    width: 10px;
    height: 10px;
  }
}
</style>